
import React, { useState } from 'react';
import { User } from '../types';
import { dbService, authService } from '../lib/firebase';
import { Activity, Lock, Mail, ArrowRight, Loader2, UserPlus, LogIn, AlertCircle } from 'lucide-react';

interface LoginProps {
  onLogin: (user: User) => void;
}

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Role selection is less relevant now as it inherits from the authorized list/admin setting, 
  // but we keep it in state for the profile creation call in case of overrides/defaults for allowed-but-undefined users.
  const [role, setRole] = useState<'Attending' | 'Resident' | 'Tech' | 'Other'>('Resident');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
        if (isSignUp) {
            // Sign Up Logic
            if (!email || !password || !lastName || !firstName) {
                throw new Error("Please fill in all required fields.");
            }
            if (password.length < 6) {
                throw new Error("Password must be at least 6 characters.");
            }

            // --- REINSTATED NAME CHECK ---
            // Fetch current allowed users (Admin created + Hardcoded list)
            const allowedUsers = await dbService.getUsers();
            
            const targetName = `${firstName.trim().toLowerCase()} ${lastName.trim().toLowerCase()}`;
            const isAuthorized = allowedUsers.some(u => {
                const uName = `${u.firstName?.trim().toLowerCase() || ''} ${u.lastName.trim().toLowerCase()}`;
                return uName.trim() === targetName;
            });

            if (!isAuthorized) {
                throw new Error("Access Denied: Your name is not on the authorized personnel list. Please ask an Administrator to create a profile for you first.");
            }
            // -----------------------------

            const cred = await authService.signup(email, password);
            // Create profile in Firestore (merging with existing placeholder if present)
            await dbService.createUserProfile(cred.user.uid, {
                firstName,
                lastName,
                email,
                role
            });
            
            // CRITICAL FIX: Manually fetch profile and trigger login to avoid race condition
            // where AuthListener fires before Firestore profile is fully written.
            const newProfile = await dbService.getUserProfile(cred.user.uid);
            if (newProfile) {
                onLogin(newProfile);
            } else {
                // Fallback if fetch fails immediately (rare), construct a temp object so they can get in
                onLogin({
                    id: cred.user.uid,
                    firstName,
                    lastName,
                    email,
                    role,
                    isAdmin: false,
                    allowedViews: ['dashboard', 'inpatients', 'reports', 'caselogs', 'calendar', 'labs', 'education', 'drugs', 'settings']
                });
            }

        } else {
            // Login Logic
            await authService.login(email, password);
            // Standard login relies on the App.tsx listener which is fine for existing users
        }
    } catch (err: any) {
        console.error(err);
        let msg = "Authentication failed.";
        if (err.code === 'auth/invalid-credential') msg = "Invalid email or password.";
        else if (err.code === 'auth/email-already-in-use') msg = "Email already registered. Please Sign In instead.";
        else if (err.code === 'auth/weak-password') msg = "Password is too weak. Must be at least 6 characters.";
        else if (err.message) msg = err.message;
        setError(msg);
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-600 rounded-full blur-[120px] opacity-30 animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-600 rounded-full blur-[120px] opacity-30 animate-pulse delay-1000"></div>
      </div>

      <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-md border border-white/20 relative z-10 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-blue-500 to-teal-400"></div>
        
        {/* Header */}
        <div className="p-8 pb-0 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-600 shadow-lg shadow-indigo-500/40 mb-4">
                <Activity className="w-8 h-8 text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-white brand-font mb-1">Mission-OP</h1>
            <p className="text-slate-300 text-xs font-medium tracking-wide">Surgical Residency Portal</p>
        </div>

        {/* Toggle Tabs */}
        <div className="flex px-8 mt-6">
            <button 
                onClick={() => { setIsSignUp(false); setError(''); }}
                className={`flex-1 pb-2 text-sm font-bold border-b-2 transition-colors ${!isSignUp ? 'text-white border-indigo-500' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
            >
                Sign In
            </button>
            <button 
                onClick={() => { setIsSignUp(true); setError(''); }}
                className={`flex-1 pb-2 text-sm font-bold border-b-2 transition-colors ${isSignUp ? 'text-white border-indigo-500' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
            >
                Create Account
            </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-4">
          
          {isSignUp && (
            <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-right-4 duration-300">
                 <div className="space-y-1">
                    <label className="text-[10px] font-bold text-indigo-200 uppercase tracking-widest ml-1">First Name</label>
                    <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Jane"
                        required={isSignUp}
                        className="w-full p-3 bg-slate-900/50 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-white placeholder-slate-500 transition-all text-sm"
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-[10px] font-bold text-indigo-200 uppercase tracking-widest ml-1">Last Name</label>
                    <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Doe"
                        required={isSignUp}
                        className="w-full p-3 bg-slate-900/50 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-white placeholder-slate-500 transition-all text-sm"
                    />
                </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-indigo-200 uppercase tracking-widest ml-1">Email Address</label>
            <div className="relative group">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="doctor@example.com"
                required
                className="w-full p-3 pl-10 bg-slate-900/50 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-white placeholder-slate-500 transition-all backdrop-blur-sm"
              />
              <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-3.5 group-focus-within:text-indigo-400 transition-colors" />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-indigo-200 uppercase tracking-widest ml-1">Password</label>
            <div className="relative group">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full p-3 pl-10 bg-slate-900/50 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-white placeholder-slate-500 transition-all backdrop-blur-sm"
              />
              <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-3.5 group-focus-within:text-indigo-400 transition-colors" />
            </div>
          </div>

          {isSignUp && (
             <div className="space-y-1 animate-in slide-in-from-right-4 duration-300">
                <label className="text-[10px] font-bold text-indigo-200 uppercase tracking-widest ml-1">Requested Role</label>
                <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setRole('Resident')} className={`p-2 rounded-lg text-sm font-bold border transition-colors ${role === 'Resident' ? 'bg-indigo-600 text-white border-indigo-500' : 'bg-slate-900/50 text-slate-400 border-slate-700/50'}`}>Resident</button>
                    <button type="button" onClick={() => setRole('Attending')} className={`p-2 rounded-lg text-sm font-bold border transition-colors ${role === 'Attending' ? 'bg-indigo-600 text-white border-indigo-500' : 'bg-slate-900/50 text-slate-400 border-slate-700/50'}`}>Attending</button>
                    <button type="button" onClick={() => setRole('Tech')} className={`p-2 rounded-lg text-sm font-bold border transition-colors ${role === 'Tech' ? 'bg-indigo-600 text-white border-indigo-500' : 'bg-slate-900/50 text-slate-400 border-slate-700/50'}`}>Tech</button>
                    <button type="button" onClick={() => setRole('Other')} className={`p-2 rounded-lg text-sm font-bold border transition-colors ${role === 'Other' ? 'bg-indigo-600 text-white border-indigo-500' : 'bg-slate-900/50 text-slate-400 border-slate-700/50'}`}>Other</button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1 italic">
                    Note: Your role may be overridden by the system admin settings.
                </p>
            </div>
          )}

          {error && (
            <div className="text-red-300 text-xs bg-red-900/20 p-3 rounded-xl border border-red-500/30 flex items-center gap-2 animate-pulse">
               <AlertCircle className="w-4 h-4 shrink-0" />
               {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-indigo-900/50 transition-all transform hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 mt-4 group disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (isSignUp ? <UserPlus className="w-5 h-5" /> : <LogIn className="w-5 h-5" />)}
            {loading ? 'Processing...' : (isSignUp ? 'Verify & Create' : 'Sign In')}
            {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
          </button>
        </form>

        <div className="p-4 text-center border-t border-white/5 bg-slate-900/30">
          <p className="text-[10px] text-slate-500 font-medium">Secure Access • Mission & Overland Park</p>
        </div>
      </div>
    </div>
  );
};
